import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { slug: "", name_id: "", name_en: "", description_id: "", description_en: "", icon: "compass", order: 0 };

export default function AdminCategories() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const { lang } = useLang();

  const load = () => api.get("/admin/categories").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing}`, form);
        toast.success(lang === "id" ? "Diperbarui" : "Updated");
      } else {
        await api.post("/admin/categories", form);
        toast.success(lang === "id" ? "Ditambahkan" : "Added");
      }
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const remove = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  const edit = (c) => { setEditing(c.id); setForm({ slug: c.slug, name_id: c.name_id, name_en: c.name_en, description_id: c.description_id, description_en: c.description_en, icon: c.icon, order: c.order || 0 }); };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl tracking-tight mb-8">{lang === "id" ? "Kategori" : "Categories"}</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 p-6 rounded-2xl border border-border bg-card space-y-3">
          <div className="font-display text-lg">{editing ? (lang === "id" ? "Edit Kategori" : "Edit Category") : (lang === "id" ? "Kategori Baru" : "New Category")}</div>
          {["slug","name_id","name_en","description_id","description_en","icon"].map((k) => (
            <div key={k}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{k}</Label>
              <Input data-testid={`cat-input-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-10" />
            </div>
          ))}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">order</Label>
            <Input data-testid="cat-input-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} className="h-10" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="cat-save-btn" onClick={save} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
              <Plus className="w-4 h-4 mr-1" /> {editing ? (lang === "id" ? "Simpan" : "Save") : (lang === "id" ? "Tambah" : "Add")}
            </Button>
            {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm(empty); }}>{lang === "id" ? "Batal" : "Cancel"}</Button>}
          </div>
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left p-4">Slug</th><th className="text-left p-4">{lang === "id" ? "Nama" : "Name"}</th><th className="text-right p-4"></th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((c) => (
                <tr key={c.id} data-testid={`cat-row-${c.slug}`}>
                  <td className="p-4 font-mono text-xs">{c.slug}</td>
                  <td className="p-4">{lang === "id" ? c.name_id : c.name_en}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="ghost" onClick={() => edit(c)} data-testid={`cat-edit-${c.slug}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(c.id)} data-testid={`cat-delete-${c.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
